import { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import { isSameDay, startOfMonth, endOfMonth } from "date-fns";
import { DailyWorkRepository, DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { useAppSelector, useAppDispatch } from "@/lib/store";
import { setCalendarSelectedDate } from "@/lib/config.slice";

export interface UseDailyWorkCalendarOptions {
  maxDays?: number;
  initialDate?: Date;
  currentMonth?: Date;
}

export const useDailyWorkCalendar = (options: UseDailyWorkCalendarOptions = {}) => {
  const { maxDays = 500, currentMonth } = options;

  const dispatch = useAppDispatch();
  const selectedDateString = useAppSelector((state) => state.config.calendarSelectedDate);
  const selectedDate = useMemo(() => moment(selectedDateString).toDate(), [selectedDateString]);

  const setSelectedDate = useCallback(
    (date: Date) => {
      dispatch(setCalendarSelectedDate(date.toISOString()));
    },
    [dispatch]
  );

  const [allDays, setAllDays] = useState<DailyWorkDayJoin[]>([]);
  const [daysForSelectedDate, setDaysForSelectedDate] = useState<DailyWorkDayJoin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeOrg = useAppSelector((state) => {
    const activeOrgId = state.organization.activeOrgId;
    return state.organization.organizations.find((org) => org.id === activeOrgId);
  });

  const monthKey = useMemo(() => {
    const monthToUse = currentMonth || selectedDate;
    return moment(monthToUse).format("YYYY-MM");
  }, [currentMonth, selectedDate]);

  const loadDays = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const monthToUse = currentMonth || selectedDate;
      const monthStart = startOfMonth(monthToUse);
      const monthEnd = endOfMonth(monthToUse);

      const response = await DailyWorkRepository.listDaysForVendor(1, maxDays, {
        startDate: moment(monthStart).format("YYYY-MM-DD"),
        endDate: moment(monthEnd).format("YYYY-MM-DD"),
      });

      setAllDays(response.data);
    } catch (err) {
      console.error("Failed to load daily work days:", err);
      setError(err instanceof Error ? err.message : "Failed to load daily work days");
      setAllDays([]);
    } finally {
      setLoading(false);
    }
  }, [maxDays, monthKey]);

  const updateSelectedDateDays = useCallback(
    (days: DailyWorkDayJoin[], date: Date) => {
      const filtered = days.filter((day) => isSameDay(new Date(day.work_date + "T12:00:00"), date));
      setDaysForSelectedDate(filtered);
    },
    []
  );

  const getDaysForDate = useCallback(
    (date: Date): DailyWorkDayJoin[] => {
      return allDays.filter((day) => isSameDay(new Date(day.work_date + "T12:00:00"), date));
    },
    [allDays]
  );

  const getDayCount = useCallback(
    (date: Date): number => {
      return getDaysForDate(date).length;
    },
    [getDaysForDate]
  );

  const hasDays = useCallback(
    (date: Date): boolean => {
      return getDayCount(date) > 0;
    },
    [getDayCount]
  );

  useEffect(() => {
    if (activeOrg) {
      loadDays();
    }
  }, [activeOrg, monthKey, loadDays]);

  useEffect(() => {
    if (!loading) {
      updateSelectedDateDays(allDays, selectedDate);
    }
  }, [updateSelectedDateDays, allDays, selectedDate, loading]);

  return {
    selectedDate,
    setSelectedDate,
    allDays,
    daysForSelectedDate,
    loading,
    error,
    getDaysForDate,
    getDayCount,
    hasDays,
    loadDays,
  };
};
